import clsx from 'clsx'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useState } from 'react'

import emptyReviewsImage from 'assets/emptyReviews.png'

import { Button } from '@/shared/ui'
import { SegmentedControl } from '@/shared/ui/segmented-control'

import { ProductReviewsResponse } from '../model/types'
import { useProductReviews } from '../model/use-product-reviews'

import { ReviewsMediaSlider, ReviewsMediaSliderSkeleton } from './media-slider/ReviewsMediaSlider'
import { ProductReviewsModals } from './ProductReviewsModals'
import { RatingSummary } from './rating-summary/RatingSummary'
import { RatingSummarySkeleton } from './rating-summary/RatingSummarySkeleton'
import { ReviewCard } from './review-card/ReviewCard'
import { ReviewFilters } from './review-filters/ReviewFilters'
import { ProductReviewListSkeleton } from './review-list-skeleton/ProductReviewListSkeleton'

import styles from './ProductReviews.module.scss'

export const PRODUCT_REVIEWS_SECTION_ID = 'productReviews'

type ProductReviewsProps = {
	productCode: number
	productCodes: number[]
	productTitle: string
	productImages?: string[]
	token?: string
	initialData: ProductReviewsResponse
	isProductUnavailable?: boolean
	isLoading?: boolean
	hideBonusButton?: boolean
	hasOwnCurrentVariantReview?: boolean
}

export const ProductReviewsSkeleton = ({ productTitle }: { productTitle: string }) => (
	<section className={styles.root} aria-labelledby="product-reviews-title">
		<header className={styles.titleRow}>
			<h2 id="product-reviews-title">Отзывы {productTitle}</h2>
		</header>

		<div className={styles.layout}>
			<RatingSummarySkeleton />
			<div className={styles.content}>
				<ReviewsMediaSliderSkeleton />
				<ProductReviewListSkeleton />
			</div>
		</div>
	</section>
)

const EmptyReviewsState = ({ onCreateReview }: { onCreateReview: () => void }) => (
	<div className={styles.emptyState}>
		<Image src={emptyReviewsImage} width={254} height={128} alt="Отзывы покупателей" />
		<div>
			<h2>Отзывов пока нет</h2>
			<p>Поделитесь впечатлениями о&nbsp;товаре с&nbsp;помощью отзыва</p>
		</div>
		<Button fullWidth onClick={onCreateReview}>
			Написать отзыв
		</Button>
	</div>
)

export const ProductReviews = ({
	productCode,
	productCodes,
	productTitle,
	productImages = [],
	token,
	initialData,
	isProductUnavailable,
	isLoading,
	hideBonusButton = true,
	hasOwnCurrentVariantReview = false
}: ProductReviewsProps) => {
	const searchParams = useSearchParams()
	const { reviews, filters, voting, media, reviewForm, authorization, moderation, success } = useProductReviews({
		productCode,
		productCodes,
		productImages,
		token,
		initialData
	})
	const { data, visible: visibleReviews, isRefreshing, isReplacing } = reviews
	const { variant, changeVariant, changeSortFilter, changeRatingFilter, showMore, sortFilterValue, ratingFilterValue } =
		filters
	const {
		items: reviewMediaItems,
		total: reviewMediaTotal,
		isLoadingMore: isLoadingMoreMedia,
		loadMore: loadMoreMedia,
		openReview: openReviewMedia,
		modal: mediaModal
	} = media
	const [isMediaCatalogueOpen, setIsMediaCatalogueOpen] = useState(false)
	const [editingReview, setEditingReview] = useState<ProductReviewsResponse['reviews'][number] | null>(null)
	const [highlightedReviewId, setHighlightedReviewId] = useState<string | null>(null)
	const [dismissedFocusedReviewId, setDismissedFocusedReviewId] = useState<string | null>(null)
	const hasOwnReview = data.reviews.some(
		review => review.isUserReview && (!review.productCode || review.productCode === productCode)
	)
	const hasReviews = data.summary.total > 0 || data.reviews.length > 0
	const hideCreateReviewButton = hasOwnCurrentVariantReview || hasOwnReview
	const hasActiveReviewFilters = sortFilterValue !== 'newest' || ratingFilterValue.length > 0
	const shouldShowStandaloneEmptyState = !isReplacing && !hasActiveReviewFilters && !hasReviews && variant === 'all'
	const shouldShowReviewFilters = hasActiveReviewFilters || isReplacing || visibleReviews.length > 0
	const focusedReviewId = useMemo(() => {
		const reviewId = searchParams.get('reviewId') ?? searchParams.get('review_id') ?? searchParams.get('reviews_id')

		return typeof reviewId === 'string' && reviewId && reviewId !== dismissedFocusedReviewId ? reviewId : null
	}, [dismissedFocusedReviewId, searchParams])
	useEffect(() => {
		if (!focusedReviewId || highlightedReviewId === focusedReviewId) return

		const reviewElement = document.querySelector(`[data-review-id="${focusedReviewId}"]`)
		if (!reviewElement) return

		const observer = new IntersectionObserver(
			entries => {
				if (!entries.some(entry => entry.isIntersecting)) return

				setHighlightedReviewId(focusedReviewId)
				observer.disconnect()
			},
			{ threshold: 0.4 }
		)

		observer.observe(reviewElement)

		return () => observer.disconnect()
	}, [focusedReviewId, highlightedReviewId, visibleReviews])

	const clearFocusedReviewQuery = () => {
		if (!focusedReviewId) return

		setDismissedFocusedReviewId(focusedReviewId)
		setHighlightedReviewId(null)
	}

	const resetReviewsViewState = () => {
		clearFocusedReviewQuery()
		mediaModal.close()
	}

	const handleReviewUpdated = (_: number, updatedReview?: ProductReviewsResponse['reviews'][number]) => {
		if (updatedReview) reviewForm.updateReview(updatedReview)
		setEditingReview(null)
	}

	if (isLoading) {
		return (
			<section id={PRODUCT_REVIEWS_SECTION_ID} className={styles.root} aria-labelledby="product-reviews-title">
				<header className={styles.titleRow}>
					<h2 id="product-reviews-title">Отзывы {productTitle}</h2>
				</header>

				<div className={styles.layout}>
					<RatingSummarySkeleton />
					<div className={styles.content}>
						<ReviewsMediaSliderSkeleton />
						<ProductReviewListSkeleton />
					</div>
				</div>
			</section>
		)
	}

	return (
		<section
			id={PRODUCT_REVIEWS_SECTION_ID}
			className={styles.root}
			aria-labelledby={hasReviews ? 'product-reviews-title' : undefined}>
			{shouldShowStandaloneEmptyState ? (
				<EmptyReviewsState onCreateReview={reviewForm.request} />
			) : (
				<>
					<header className={styles.titleRow}>
						<h2 id="product-reviews-title">Отзывы {productTitle}</h2>
					</header>

					<div className={styles.layout}>
						<RatingSummary
							data={data.summary}
							isProductUnavailable={isProductUnavailable}
							onCreateReview={reviewForm.request}
							hideCreateReviewButton={hideCreateReviewButton}
							hideBonusButton={hideBonusButton}
						/>

						<div className={styles.content}>
							<SegmentedControl
								className={clsx(styles.variantSwitch, {
									[styles.withoutMedia]: !reviewMediaItems.length && !isReplacing
								})}
								size="sm"
								options={data.variants.map(item => ({ value: item.id, label: item.name }))}
								value={variant}
								onChange={value => {
									resetReviewsViewState()
									changeVariant(value as typeof variant)
								}}
							/>

							{isReplacing ? (
								<ReviewsMediaSliderSkeleton />
							) : (
								reviewMediaItems.length > 0 && (
									<ReviewsMediaSlider
										items={reviewMediaItems}
										total={reviewMediaTotal}
										onOpen={mediaModal.open}
										onOpenCatalogue={() => setIsMediaCatalogueOpen(true)}
									/>
								)
							)}

							{shouldShowReviewFilters && (
								<ReviewFilters
									sortValue={sortFilterValue}
									ratingValue={ratingFilterValue}
									onSortChange={value => {
										resetReviewsViewState()
										changeSortFilter(value)
									}}
									onRatingChange={value => {
										resetReviewsViewState()
										changeRatingFilter(value)
									}}
								/>
							)}

							{isReplacing ? (
								<ProductReviewListSkeleton />
							) : visibleReviews.length === 0 ? (
								<EmptyReviewsState onCreateReview={reviewForm.request} />
							) : (
								<>
									<div className={styles.reviewList} aria-busy={isRefreshing}>
										{visibleReviews.map(review => (
											<ReviewCard
												key={review.id}
												className={
													focusedReviewId && highlightedReviewId === review.id ? styles.reviewFocused : undefined
												}
												isHighlighted={
													review.isUserReview || Boolean(focusedReviewId && highlightedReviewId === review.id)
												}
												review={review}
												onVote={value => voting.request(review.id, value)}
												isVoteDisabled={review.isUserReview}
												onOpenMedia={(photoIndex, src) => openReviewMedia(review.id, photoIndex, src)}
												onEdit={review.isUserReview ? setEditingReview : undefined}
											/>
										))}
									</div>

									{data.hasMore && (
										<Button
											className={styles.showMoreButton}
											theme="outlined"
											size="L"
											onClick={showMore}
											isLoading={isRefreshing}
											fullWidth>
											Показать еще
										</Button>
									)}
								</>
							)}
						</div>
					</div>
				</>
			)}
			<ProductReviewsModals
				productCode={productCode}
				productName={productTitle}
				token={token}
				onVote={voting.request}
				reviewForm={reviewForm}
				editReview={{
					review: editingReview,
					close: () => setEditingReview(null),
					handleSubmitted: handleReviewUpdated
				}}
				authorization={authorization}
				moderation={moderation}
				success={success}
				media={{
					items: reviewMediaItems,
					total: reviewMediaTotal,
					initialIndex: mediaModal.index ?? 0,
					isOpen: mediaModal.index !== null,
					close: mediaModal.close,
					loadMore: loadMoreMedia,
					isLoadingMore: isLoadingMoreMedia
				}}
				mediaCatalogue={{
					isOpen: isMediaCatalogueOpen,
					close: () => setIsMediaCatalogueOpen(false),
					openMedia: mediaModal.open,
					hasMore: reviewMediaItems.length < reviewMediaTotal
				}}
			/>
		</section>
	)
}
